import { Component, type ReactNode, type ErrorInfo } from 'react';
import { t, isRTL } from '../../utils/i18n';
import type { Lang } from '../../types';

interface Props {
  children: ReactNode;
  lang?: Lang;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function getLang(propLang?: Lang): Lang {
  if (propLang) return propLang;
  try {
    const stored = localStorage.getItem('bagrut_lang');
    if (stored === 'en' || stored === 'he' || stored === 'ar' || stored === 'ru') return stored;
  } catch { /* ignore */ }
  return 'he';
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);

    // Report to Sentry if available
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        extra: { componentStack: info.componentStack },
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    // Clear any corrupted state
    try {
      sessionStorage.clear();
    } catch { /* ignore */ }
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const lang = getLang(this.props.lang);
      const rtl = isRTL(lang);

      return (
        <div
          dir={rtl ? 'rtl' : 'ltr'}
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#0f172a',
            color: '#e2e8f0',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            padding: '2rem',
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>😵</div>
            <h1 style={{ fontSize: '1.4rem', color: '#f87171', marginBottom: '0.5rem' }}>
              {t('error_boundary_title', lang)}
            </h1>
            <p style={{ color: '#94a3b8', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              {t('error_boundary_subtitle', lang)}
            </p>
            {this.state.error && (
              <p
                style={{
                  background: '#1e293b',
                  borderRadius: 8,
                  padding: '0.75rem',
                  fontSize: '0.8rem',
                  color: '#64748b',
                  marginBottom: '1.5rem',
                  direction: 'ltr',
                  textAlign: 'left',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error.message}
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={this.handleRetry}
                style={{
                  padding: '0.6rem 1.5rem',
                  borderRadius: 8,
                  border: 'none',
                  background: '#7c3aed',
                  color: 'white',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                {t('error_boundary_retry', lang)}
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '0.6rem 1.5rem',
                  borderRadius: 8,
                  border: '1px solid #334155',
                  background: 'transparent',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                {t('error_boundary_back', lang)}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
